import { getCsrfToken } from './auth';

/**
 * The collaboration client: one place that talks to `/api/collab/*` and `/api/users/*`,
 * plus the two pieces of cross-screen wiring presence needs.
 *
 * ⚠️ Why the "where" and the open-chat request live in MODULE scope rather than in React
 * state: `App` re-renders every second (Critical Note #59), and the collaboration poll is
 * mounted directly under it. Anything the poll reads that is recreated on each render —
 * a callback, an object literal — restarts the poll effect sixty times a minute. So the
 * two values that flow *into* the poll from elsewhere in the tree are kept here, outside
 * React, and read at poll time. Nothing subscribes to them, so nothing re-renders.
 */

async function call(method, url, body) {
  const opts = { method, headers: {}, credentials: 'include' };
  if (body !== undefined) {
    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  if (method !== 'GET') opts.headers['X-CSRF-Token'] = getCsrfToken();

  const res = await fetch(url, opts);
  let data = null;
  try { data = await res.json(); } catch (_) { /* a 500 can arrive as HTML */ }

  if (!res.ok) {
    // FastAPI puts the reason in `detail`, and these endpoints write it as a sentence a
    // person can act on ("Ruth has chat turned off."). Surface it verbatim — that refusal
    // is the whole point of the `off` visibility state.
    const err = new Error(data?.detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

/* ------------------------------------------------------------------ *
 * How a person is drawn
 * ------------------------------------------------------------------ */

/**
 * The circle style for one person: their own colour, filled, with white initials.
 *
 * The server stores one saturated colour per user (`users.avatar_color`), stable for life, so
 * the same person is the same colour on every screen and after every restart.
 *
 * This was briefly a light wash of that colour with the initials at full strength. It read
 * well in isolation and badly in place: against the app's pale lavender header a pale disc
 * has almost no edge, and a row of them stopped looking like people at all. Solid discs with
 * white initials are what the design called for, and they are what carries at 32px.
 *
 * Returned as CSS custom properties, which is what `Collab.css` reads — so a caller cannot
 * set the ground without the matching ink.
 */
export function avatarStyle(hex) {
  const clean = /^#?[0-9a-f]{6}$/i.test(String(hex || '')) ? String(hex).replace('#', '') : '5C35A8';
  return { '--collab-tint': `#${clean}`, '--collab-ink': '#ffffff' };
}

/* ------------------------------------------------------------------ *
 * Where this tab is looking
 * ------------------------------------------------------------------ */

// IDs only — never a title. The presence line a colleague sees is resolved on the
// server against THAT colleague's privacy scope (Critical Note #71), so a private
// panel's name cannot travel out of this tab in the first place.
let currentWhere = { module: '', panelId: '' };

/** Tell the poll where this tab is. Safe to call on every render — it is a plain
 *  assignment and nothing subscribes to it. */
export function setCollabWhere(module, panelId) {
  currentWhere = { module: module || '', panelId: panelId == null ? '' : String(panelId) };
}

export function getCollabWhere() {
  return currentWhere;
}

/* ------------------------------------------------------------------ *
 * What the screen would share
 * ------------------------------------------------------------------ */

// The page that is showing registers a GETTER, and the dock calls it at the moment the
// user clicks "Share this view". A matrix page re-renders constantly while somebody is
// paging and expanding; pushing that state outward on every change would be work done
// hundreds of times for an answer needed once.
let viewStateGetter = null;

export function registerViewState(fn) {
  viewStateGetter = fn;
  return () => { if (viewStateGetter === fn) viewStateGetter = null; };
}

export function readViewState() {
  if (!viewStateGetter) return {};
  try {
    const val = viewStateGetter();
    return val && typeof val === 'object' ? val : {};
  } catch (_) {
    return {};
  }
}

/* ------------------------------------------------------------------ *
 * "Open a chat about this" — from anywhere in the tree
 * ------------------------------------------------------------------ */

const openListeners = new Set();

/**
 * Ask the dock to open on `userId`, optionally carrying a context card to send.
 * Used by the matrix row menu and the panel's Share-this-view action, which are far
 * from the dock in the tree and must not have to thread a callback down to it.
 */
export function requestChat(userId, context) {
  openListeners.forEach((fn) => {
    try { fn(userId, context || null); } catch (_) { /* one bad listener must not stop the rest */ }
  });
}

export function onChatRequest(fn) {
  openListeners.add(fn);
  return () => openListeners.delete(fn);
}

/* ------------------------------------------------------------------ *
 * Building a context card from the current screen
 * ------------------------------------------------------------------ */

/** Encode a context object for a URL. base64url so it survives a paste. */
export function encodeContext(ctx) {
  try {
    const json = JSON.stringify(ctx);
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (_) {
    return '';
  }
}

/** Decode a `?ctx=` parameter. Returns null for anything that will not read as one —
 *  a malformed card must render nothing, never throw on mount. */
export function decodeContext(raw) {
  if (!raw) return null;
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(b64)));
    const val = JSON.parse(json);
    return val && typeof val === 'object' ? val : null;
  } catch (_) {
    return null;
  }
}

/** Where a context card points, as an in-app path. */
export function contextHref(ctx) {
  if (!ctx || !ctx.route) return '';
  const enc = encodeContext({
    state: ctx.state || {},
    focus: ctx.focus || {},
    kind: ctx.kind || 'view',
  });
  return enc ? `${ctx.route}?ctx=${enc}` : ctx.route;
}

export const collabApi = {
  poll: ({ since = '', module = '', panelId = '' } = {}) => {
    const q = new URLSearchParams();
    if (since) q.set('since', since);
    if (module) q.set('module', module);
    if (panelId) q.set('panel_id', panelId);
    const qs = q.toString();
    return call('GET', `/api/collab/poll${qs ? `?${qs}` : ''}`);
  },
  send: (to, body, context) => call('POST', '/api/collab/send', { to, body, context }),
  markRead: (withUser) => call('POST', '/api/collab/read', { with_user: withUser }),

  /** One page of a thread. `before` walks backwards a page at a time; `around` asks for
   *  a window centred on one message, which is what a search hit opens. */
  thread: (other, { limit, before, around } = {}) => {
    const q = new URLSearchParams();
    if (limit) q.set('limit', String(limit));
    if (before) q.set('before', before);
    if (around) q.set('around', around);
    const qs = q.toString();
    return call('GET', `/api/collab/thread/${encodeURIComponent(other)}${qs ? `?${qs}` : ''}`);
  },

  searchThread: (other, query) =>
    call('GET', `/api/collab/search/${encodeURIComponent(other)}?q=${encodeURIComponent(query)}`),

  editMessage: (id, body) => call('POST', '/api/collab/edit', { id, body }),
  deleteMessage: (id) => call('POST', '/api/collab/delete', { id }),
  me: () => call('GET', '/api/users/me'),
  savePrefs: (prefs) => call('POST', '/api/users/me/prefs', { prefs }),
  directory: () => call('GET', '/api/users/directory'),
  settings: () => call('GET', '/api/collab/settings'),
  saveSettings: (values) => call('POST', '/api/collab/settings', values),
};
